/**
 * Every permission the panel checks — a MIRROR of `ADMIN_PERMISSION` in
 * bella-admin-api `src/modules/admin-access/admin-permissions.ts`, which owns
 * the list. Copied because bella is not a dependency of this app.
 *
 * Drift fails safe: an unknown key is never in the admin's list, so a key
 * added here first hides its screen, and a key added there first is simply not
 * checked here yet. The server is the real gate either way.
 *
 * One `<screen>.view` per screen, plus one key per action some admins may be
 * refused. Which admin holds what is decided on the server and arrives through
 * `usePermissions()` — never infer it from the role here.
 */
export const Permission = {
  TASKS_VIEW: "tasks.view",
  TASKS_RAISE: "tasks.raise",
  TASKS_RAISE_FOR_OTHERS: "tasks.raise-for-others",
  TASKS_REASSIGN: "tasks.reassign",

  TEAM_TASKS_VIEW: "team-tasks.view",
  TEAM_TASKS_ASSIGN: "team-tasks.assign",

  CALL_LOGS_VIEW: "call-logs.view",
  CALL_LOGS_LISTEN: "call-logs.listen",
  CALL_LOGS_TRANSCRIBE: "call-logs.transcribe",
  CALL_LOGS_BULK_TRANSCRIBE: "call-logs.bulk-transcribe",
  CALL_LOGS_EXPORT: "call-logs.export",

  CALLS_OVERVIEW_VIEW: "calls-overview.view",
  AGENT_CALL_DASHBOARD_VIEW: "agent-call-dashboard.view",
  CALLING_NUMBERS_VIEW: "calling-numbers.view",

  // Call-backs: Sarvam callers to ring back, and marking them called back.
  CALLBACKS_VIEW: "callbacks.view",

  // 2nd Vehicle Leads: customers who said yes to a 2nd vehicle; call them, set status and note.
  SECOND_VEHICLE_LEADS_VIEW: "second-vehicle-leads.view",

  BOTS_VIEW: "bots.view",
  BOTS_EDIT: "bots.edit",

  TRANSCRIPTION_WORDS_VIEW: "transcription-words.view",
  TRANSCRIPTION_WORDS_EDIT: "transcription-words.edit",

  PAYMENTS_DIGEST_VIEW: "payments-digest.view",
  PAYMENTS_DIGEST_MANAGE: "payments-digest.manage",
  HOURLY_STATS_VIEW: "hourly-stats.view",
  HOURLY_STATS_MANAGE: "hourly-stats.manage",
  // Failures of important APIs and jobs: the list, and who gets the Telegram alerts.
  FAILURE_ALERTS_VIEW: "failure-alerts.view",
  FAILURE_ALERTS_MANAGE: "failure-alerts.manage",

  SUPPORT_INSIGHTS_VIEW: "support-insights.view",

  // The Tulsi dashboard (sql/051): every page and "Done"; the issue log's edits.
  TULSI_VIEW: "tulsi.view",
  TULSI_MANAGE: "tulsi.manage",

  // The Anju dashboard (sql/062): every page and "Done"; the issue log; prompt uploads.
  ANJU_VIEW: "anju.view",
  ANJU_MANAGE: "anju.manage",
  ANJU_PROMPTS: "anju.prompts",
  // The Sheetal dashboard (sql/055): every page and "Done"; the issue log and prompt uploads.
  SHEETAL_VIEW: "sheetal.view",
  SHEETAL_MANAGE: "sheetal.manage",

  AUTOMATION_VIEW: "automation.view",
  AUTOMATION_MANAGE: "automation.manage",
  AUTOMATED_MESSAGES_VIEW: "automated_messages.view",
  AUTOMATED_MESSAGES_MANAGE: "automated_messages.manage",

  // Change the speak-and-fix-English limits. Using it needs nothing; the
  // Automation section reads `canManage` from the server.
  DICTATION_MANAGE: "dictation.manage",

  TODO_VIEW: "todo.view",
  TODO_MANAGE: "todo.manage",

  HOME_POPUPS_VIEW: "home-popups.view",
  HOME_POPUPS_MANAGE: "home-popups.manage",

  // Dialogues (in-app popups and surveys). Mirrors bella; bang_common sql/070.
  DIALOGUES_VIEW: "dialogues.view",
  DIALOGUES_MANAGE: "dialogues.manage",

  // Referral campaigns. The Insight tab lists customer phone numbers, so it has its own key.
  REFERRALS_VIEW: "referrals.view",
  REFERRALS_MANAGE: "referrals.manage",
  REFERRALS_INSIGHTS: "referrals.insights",

  SERVICE_JOBS_VIEW: "service-jobs.view",
  SERVICE_JOBS_MANAGE: "service-jobs.manage",

  JOB_LIST_VIEW: "job-list.view",

  CHAMP_ASSIGNMENT_PREVIEW: "champ-assignment.preview",

  // Matcher (permanent assignment): see its page; change its settings (named people).
  MATCHER_VIEW: "matcher.view",
  MATCHER_RULES_EDIT: "matcher.rules.edit",
  // Customers without a champ: see the page and assign (anyone who sees it may).
  MATCHER_BACKLOG: "matcher-backlog.view",

  // The WhatsApp inbox and the docked panel — every business number.
  WHATSAPP_VIEW: "whatsapp.view",
  // Reply, send templates and make a ticket from a chat. Counts only with WHATSAPP_VIEW.
  WHATSAPP_SEND: "whatsapp.send",

  // The Tickets screen and the ticket panel: open and work any ticket.
  TICKETS_VIEW: "tickets.view",
  // Move other people's tickets (and, later, bulk actions and the team view).
  TICKETS_MANAGE: "tickets.manage",

  // The customer panel and /customers/:id: search, profile, notes, activity.
  CUSTOMERS_VIEW: "customers.view",
  // Show a customer's whole phone numbers (the panel masks them otherwise).
  CUSTOMERS_PHONE_VIEW: "customers.phone.view",

  // The champ panel and /champs/:id: search, profile, notes, activity (sql/054).
  CHAMPS_VIEW: "champs.view",
  // Show a champ's whole phone numbers (the panel masks them otherwise).
  CHAMPS_PHONE_VIEW: "champs.phone.view",
  // Block and unblock a champ's login to the Champ App (every role).
  CHAMPS_LOGIN_BLOCK: "champs.login-block",

  // Autopilot lives in carselona_tracking. Gates the Autopilot page — opening it and every change.
  AUTOPILOT_RULES_EDIT: "autopilot.rules.edit",

  PERMISSIONS_VIEW: "permissions.view",
  PERMISSIONS_EDIT: "permissions.edit",
} as const;

export type PermissionKey = (typeof Permission)[keyof typeof Permission];
